import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { XCircle } from "react-feather";

const VARIANTS = {
  overlay: {
    open: { opacity: 1 },
    closed: { opacity: 0 },
  },
  content: {
    open: { opacity: 1, y: 0 },
    closed: { opacity: 0, y: 300 },
  },
};

interface NewTemplateModalProps {
  isVisible: boolean;
  close(): void;
  newTemplateName: string;
  updateNewTemplateName(value: string): void;
  handleSave(): void;
}

const NewTemplateModal = ({
  isVisible,
  close,
  newTemplateName,
  updateNewTemplateName,
  handleSave,
}: NewTemplateModalProps) => {
  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          key="container"
          className="fixed top-0 left-0 right-0 bottom-0 flex items-center justify-center z-20"
          initial="closed"
          animate={isVisible ? "open" : "closed"}
          exit="closed"
        >
          <motion.div
            key="overlay"
            className="absolute top-0 left-0 right-0 bottom-0 bg-black bg-opacity-40 z-30 cursor-pointer"
            variants={VARIANTS.overlay}
            onClick={() => close()}
          />
          <motion.div
            key="content"
            className="z-40"
            variants={VARIANTS.content}
          >
            <div className="bg-white shadow-lg rounded-lg w-full max-w-3xl">
              <div className="h-14 px-4 bg-gray-100 flex items-center justify-between rounded-t-lg">
                <span className="text-gray-500">Save new template</span>
                <button
                  className="text-gray-500 focus:outline-none"
                  onClick={close}
                >
                  <XCircle size={20} />
                </button>
              </div>
              <div className="px-4 py-6 font-light text-sm">
                <p>
                  It look like you have just started a new template. Go ahead
                  and save this template to make sure you do not lose any
                  progress.
                </p>
                <form
                  className="grid grid-cols-4 gap-x-4 mt-8 w-full"
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSave();
                    close();
                  }}
                >
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Template name
                    </label>
                    <input
                      type="text"
                      name="name"
                      className="mt-1 focus:ring-red-200 focus:border-red-200 block w-full sm:text-sm border-gray-300 rounded-md"
                      value={newTemplateName}
                      onChange={(evt) =>
                        updateNewTemplateName(evt.target.value)
                      }
                    />
                  </div>
                  <div className="col-span-2 self-end">
                    <button
                      type="submit"
                      className={`inline-flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white ${
                        newTemplateName.length
                          ? "bg-red-500 hover:bg-red-400"
                          : "bg-gray-300 hover:bg-gray-400"
                      } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-200`}
                      disabled={!newTemplateName.length}
                    >
                      Save template
                    </button>
                    <button
                      type="button"
                      className={`ml-2 inline-flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-gray-500 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-200`}
                      onClick={() => close()}
                    >
                      Later
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default NewTemplateModal;
